<template>
    <div class="h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
        <div class="p-4">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-4">Field Types</h3>

            <div class="space-y-2">
                <!-- Field type palette items -->
                <draggable
                    :list="availableFieldTypes"
                    :group="{ name: 'fields', pull: 'clone', put: false }"
                    :clone="cloneField"
                    :sort="false"
                    item-key="value"
                    class="space-y-2"
                >
                    <template #item="{element}">
                        <div
                            class="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-move transition-colors"
                        >
                            <div class="flex-shrink-0 text-2xl mr-3">{{ element.icon }}</div>
                            <div class="flex-1 min-w-0">
                                <div class="text-sm font-medium text-gray-900 dark:text-white">
                                    {{ element.label }}
                                </div>
                                <div class="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {{ element.description }}
                                </div>
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import draggable from 'vuedraggable';

const props = defineProps({
    templateType: {
        type: Object,
        default: null,
    },
});

const allFieldTypes = [
    { value: 'yes_no', label: 'Yes/No', icon: '✓', description: 'Yes or No choice' },
    { value: 'pass_fail_na', label: 'Pass/Fail/NA', icon: '✓✗', description: 'Pass, Fail, or N/A' },
    { value: 'multiple_choice', label: 'Multiple Choice', icon: '◉', description: 'Single selection from options' },
    { value: 'multiple_selection', label: 'Checkboxes', icon: '☑', description: 'Multiple selections' },
    { value: 'dropdown_select', label: 'Dropdown', icon: '▼', description: 'Dropdown menu' },
    { value: 'text_input', label: 'Short Text', icon: '📝', description: 'Single line text' },
    { value: 'textarea', label: 'Long Text', icon: '📄', description: 'Multi-line text' },
    { value: 'number_input', label: 'Number', icon: '#️⃣', description: 'Integer number' },
    { value: 'decimal_input', label: 'Decimal', icon: '.#', description: 'Decimal number' },
    { value: 'rating_scale', label: 'Rating', icon: '⭐', description: 'Star or numeric rating' },
    { value: 'date_picker', label: 'Date', icon: '📅', description: 'Date selection' },
    { value: 'datetime_picker', label: 'Date & Time', icon: '📅⏰', description: 'Date and time' },
    { value: 'time_picker', label: 'Time', icon: '⏰', description: 'Time selection' },
    { value: 'photo_capture', label: 'Photo', icon: '📷', description: 'Photo capture/upload' },
    { value: 'file_upload', label: 'File', icon: '📎', description: 'File upload' },
    { value: 'signature', label: 'Signature', icon: '✍️', description: 'Digital signature' },
];

// Define allowed fields for each template type
const typeFieldMapping = {
    'inspection_audit': [
        'yes_no',
        'multiple_choice',
        'multiple_selection',
        'dropdown_select',
        'text_input',
        'textarea',
        'number_input',
        'decimal_input',
        'date_picker',
        'time_picker',
        'datetime_picker',
        'photo_capture',
        'file_upload',
        'signature',
    ],
    'procedure_process': [
        'yes_no',
        'text_input',
        'number_input',
        'decimal_input',
        'date_picker',
        'time_picker',
        'datetime_picker',
        'photo_capture',
        'file_upload',
        'signature',
    ],
    'performance_testing': [
        'pass_fail_na',
        'multiple_choice',
        'dropdown_select',
        'text_input',
        'number_input',
        'decimal_input',
        'rating_scale',
        'date_picker',
        'time_picker',
        'datetime_picker',
        'photo_capture',
        'file_upload',
        'signature',
    ],
    'assessment_evaluation': [
        'yes_no',
        'multiple_choice',
        'multiple_selection',
        'dropdown_select',
        'text_input',
        'textarea',
        'number_input',
        'decimal_input',
        'rating_scale',
        'date_picker',
        'time_picker',
        'datetime_picker',
        'photo_capture',
        'file_upload',
        'signature',
    ],
    // Other types will have all fields by default for now
};

// Filter fields based on template type
const availableFieldTypes = computed(() => {
    if (!props.templateType || !props.templateType.key) {
        return allFieldTypes;
    }

    const allowedFields = typeFieldMapping[props.templateType.key];

    if (!allowedFields) {
        // If no mapping found, return all fields
        return allFieldTypes;
    }

    // Filter to only show allowed fields for this type
    return allFieldTypes.filter(field => allowedFields.includes(field.value));
});

const cloneField = (fieldType) => {
    return {
        id: Date.now(),
        label: '',
        field_type: fieldType.value,
        is_required: false,
        sort_order: 0,
        field_config: null,
        allow_notes: false,
        require_notes: false,
        allow_photos: false,
        require_photos: false,
        allow_attachments: false,
        require_attachments: false,
        is_primary: true,
        parent_field_id: null,
        dependency_rule: null,
        help_text: null,
        points: null,
        desired_answer: null,
        isNew: true,
    };
};
</script>
